import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, X, MapPin, Calendar, Users, Shield, Trophy, Dumbbell } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { fetchCityImageFromMultipleSources } from '@/lib/fetchCityImage';

interface Slide {
  id: string;
  title: string;
  description: string;
  image: string;
  icon: React.ReactNode;
  action: {
    text: string;
    onClick: () => void;
  };
}

const WelcomeCarousel: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [autoplay, setAutoplay] = useState(true);
    const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [cityImage, setCityImage] = useState<string | null>(null);
  const [userCity, setUserCity] = useState<string | null>(null);
  
  // Check if the user has seen the carousel before
  useEffect(() => {
    const checkCarouselSeen = async () => {
      if (!user) return;
      
      const seenCarousel = localStorage.getItem('welcome_carousel_seen');
      if (seenCarousel) {
        setDismissed(true);
        return;
      }
    };
    
    checkCarouselSeen();
  }, [user]);
  
  // Fetch user's city and its image
  useEffect(() => {
    const fetchUserCity = async () => {
      if (!user) return;
      
      try {
        const { data } = await supabase
          .from('users')
          .select('city')
          .eq('auth_id', user.id)
          .single();
          
        if (data?.city) {
          setUserCity(data.city);
          
          // Fetch city image
          try {
            const imageUrl = await fetchCityImageFromMultipleSources(data.city);
            setCityImage(imageUrl);
          } catch (err) {
            console.warn('Failed to fetch city image:', err);
            // Use default image as fallback
            setCityImage('https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?auto=format&fit=crop&w=800&q=80');
          }
        }
      } catch (error) {
        console.error('Error fetching user city:', error);
      }
    };
    
    fetchUserCity();
  }, [user]);
  
  // Autoplay functionality
  useEffect(() => {
    if (!autoplay || dismissed) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [autoplay, dismissed]);
  
  // Pause autoplay on hover
  const handleMouseEnter = () => setAutoplay(false);
  const handleMouseLeave = () => setAutoplay(true);
  
  // Dismiss carousel and save preference
  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('welcome_carousel_seen', 'true');
  };
  
  const getSlides = (): Slide[] => [
    {
      id: 'find-players',
      title: 'Find Players Near You',
      description: 'Connect with players in your area who share your interests and skill level.',
      image: cityImage || 'https://images.unsplash.com/photo-1560012057-4372e14c5085?auto=format&fit=crop&w=800&q=80',
      icon: <Users className="h-6 w-6 text-gold-400" />,
      action: {
        text: 'Find Players',
        onClick: () => navigate('/dashboard/quick-play-transition')
      }
    },
    {
      id: 'join-clubs',
      title: 'Join Exclusive Clubs',
      description: 'Become a member of prestigious clubs and access premium facilities.',
      image: 'https://images.unsplash.com/photo-1603722346374-d5e3c673ad3f?auto=format&fit=crop&w=800&q=80',
      icon: <Shield className="h-6 w-6 text-gold-400" />,
      action: {
        text: 'Explore Clubs',
        onClick: () => navigate('/dashboard/facilities')
      }
    },
    {
      id: 'upcoming-events',
      title: 'Discover Events',
      description: 'Find tournaments, social gatherings, and training sessions in your area.',
      image: 'https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?auto=format&fit=crop&w=800&q=80',
      icon: <Calendar className="h-6 w-6 text-gold-400" />,
      action: {
        text: 'Coming Soon',
        onClick: () => {}
      }
    },
    {
      id: 'explore-cities',
      title: 'Play Around the World',
      description: 'Discover venues and connect with players wherever you travel.',
      image: 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?auto=format&fit=crop&w=800&q=80',
      icon: <MapPin className="h-6 w-6 text-gold-400" />,
      action: {
        text: 'Explore Cities',
        onClick: () => navigate('/dashboard/facilities')
      }
    },
    {
      id: 'track-progress',
      title: 'Track Your Progress',
      description: 'Monitor your performance, achievements, and growth over time.',
      image: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=800&q=80',
      icon: <Trophy className="h-6 w-6 text-gold-400" />,
      action: {
        text: 'View Profile',
        onClick: () => navigate('/dashboard/profile')
      }
    },
    {
      id: 'elite-trainer',
      title: 'Find Elite Personal Trainers',
      description: 'Connect with certified personal trainers who can help you reach your fitness goals.',
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=800&q=80',
      icon: <Dumbbell className="h-6 w-6 text-gold-400" />,
      action: {
        text: 'Coming Soon',
        onClick: () => {}
      }
    }
  ];
  
  const slides = getSlides();
  
  if (dismissed) return null;
  
  return (
    <div 
      className="relative bg-dark-200 rounded-xl border border-dark-300 overflow-hidden mb-6"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button 
        onClick={handleDismiss}
        className="absolute top-3 right-3 z-10 p-1.5 bg-dark-300/80 hover:bg-dark-400 rounded-full text-dark-800 hover:text-white transition-colors"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      
      <div className="relative h-64 sm:h-80">
        <AnimatePresence mode="wait">
          <motion.div
            key={slides[currentSlide].id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0"
          >
            {/* Background Image with Gradient Overlay */}
            <div className="absolute inset-0">
              <img 
                src={slides[currentSlide].image} 
                alt={slides[currentSlide].id === 'find-players' ? `${userCity || 'City'} skyline` : slides[currentSlide].title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-dark-200 to-transparent" />
            </div>
            
            {/* Content */}
            <div className="relative h-full flex flex-col justify-center p-6 sm:p-8 max-w-md">
              <div className="flex items-center mb-2">
                <div className="p-2 bg-dark-300/80 rounded-full mr-3">
                  {slides[currentSlide].icon}
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-white !text-white dark:!text-white">
                  {slides[currentSlide].title}
                </h2>
              </div>
              <p className="text-white/80 mb-6">
                {slides[currentSlide].id === 'upcoming-events' ? (
                  <span className="text-white/80">Find tournaments, social gatherings, and training sessions in your area.</span>
                ) : slides[currentSlide].id === 'elite-trainer' ? (
                  <span className="text-white/80">Connect with certified personal trainers who can help you reach your fitness goals.</span>
                ) : (
                  slides[currentSlide].description
                )}
              </p>
              <Button
                disabled={slides[currentSlide].id === 'upcoming-events' || slides[currentSlide].id === 'elite-trainer'}
                onClick={slides[currentSlide].action.onClick}
                className={cn(
                  "w-full sm:w-auto",
                  slides[currentSlide].id === 'upcoming-events' || slides[currentSlide].id === 'elite-trainer'
                    ? "bg-dark-400 text-white cursor-not-allowed"
                    : "bg-gold-400 hover:bg-gold-500 text-dark-50"
                )}
              >
                {slides[currentSlide].id === 'upcoming-events' || slides[currentSlide].id === 'elite-trainer' ? 'Coming Soon' : slides[currentSlide].action.text}
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
        
        {/* Navigation Dots */}
        <div className="absolute bottom-2 left-0 right-0 flex justify-center space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={cn(
                "w-2 h-2 rounded-full transition-all",
                currentSlide === index 
                  ? "bg-gold-400 w-4" 
                  : "bg-dark-800 hover:bg-dark-700"
              )}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        
        {/* Navigation Arrows */}
        <div className="absolute bottom-2 left-0 right-0 flex justify-between px-4">
          <button
            onClick={() => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)}
            className="p-2 bg-dark-300/80 hover:bg-dark-400 rounded-full text-white !text-white dark:!text-white transition-colors"
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={() => setCurrentSlide((prev) => (prev + 1) % slides.length)}
            className="p-2 bg-dark-300/80 hover:bg-dark-400 rounded-full text-white !text-white dark:!text-white transition-colors"
            aria-label="Next slide"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeCarousel;